<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { useUserManual } from "../composables/useUserManual.ts";
import { trainerDetails } from "../main";
import ManualImageZoom from "../components/manual/ManualImageZoom.vue";
import ManualSearchHeader from "../components/manual/ManualSearchHeader.vue";

const trainer = trainerDetails;

const {
    searchTerm,
    filteredContent,
    matchCount,
    setContentContainer,
    jumpToNextMatch,
    clearSearchTerm,
    loadContent,
    zoomedImage,
    openImageFromEvent,
    closeImage,
    t,
} = useUserManual();

const stickyHeaderTop = ref("0px");
const anchorScrollMargin = ref("1rem");
const contentsStickyTop = ref("1rem");

// Stick the search header directly below the sticky main menu, keep TOC
// anchor targets clear of both, and park the sticky contents rail below them. Measured rather than hard-coded because the
// menubar wraps to more rows on narrow viewports.
function measureStickyOffsets() {
    const menuHeight = Math.ceil(document.querySelector(".sticky-menu")?.getBoundingClientRect().height ?? 0);
    const headerHeight = Math.ceil(document.querySelector(".header-section")?.getBoundingClientRect().height ?? 0);
    if (!menuHeight) return;
    stickyHeaderTop.value = `${menuHeight}px`;
    anchorScrollMargin.value = `${menuHeight + headerHeight + 16}px`;
    contentsStickyTop.value = `${menuHeight + headerHeight + 8}px`;
}

let menuResizeObserver: ResizeObserver | undefined;

onMounted(async () => {
    // Measuring straight away is unreliable: the stylesheet that sizes the
    // menubar is not guaranteed to have been applied yet, and an unstyled
    // menubar measures several times its real height, which parked the sticky
    // header in the middle of the content. Wait for a painted frame first, then
    // keep it in sync as the menubar re-wraps.
    await nextTick();

    // MainMenu only renders once the trainer resolves, so .sticky-menu may not
    // exist yet. Retry for a few frames, then track it for later re-wraps.
    let attempts = 0;
    const attach = () => {
        const menu = document.querySelector(".sticky-menu");
        if (!menu || !menu.getBoundingClientRect().height) {
            if (attempts++ < 60) requestAnimationFrame(attach);
            return;
        }
        measureStickyOffsets();
        menuResizeObserver = new ResizeObserver(measureStickyOffsets);
        menuResizeObserver.observe(menu);
    };
    requestAnimationFrame(attach);

    await loadContent();
});

onBeforeUnmount(() => menuResizeObserver?.disconnect());
</script>

<template>
    <MainMenu highlight="manual" :trainer="trainer" />
    <div class="app-wrapper mb-8">
        <div class="header-section mt-8" :style="{ top: stickyHeaderTop }">
            <ManualSearchHeader
                v-model:search-term="searchTerm"
                id-prefix="user-manual-page"
                :heading="t('profile-overview.instructions.heading')"
                :placeholder="t('fields.search')"
                :match-text="t('fields.searchMatches', { count: matchCount }, matchCount)"
                @next="jumpToNextMatch"
                @clear="clearSearchTerm"
            />
        </div>
        <div
            :ref="setContentContainer"
            :style="{ '--manual-anchor-scroll-margin': anchorScrollMargin, '--manual-sticky-top': contentsStickyTop }"
            v-html="filteredContent"
            @click="openImageFromEvent"
        ></div>
        <ManualImageZoom :image="zoomedImage" @close="closeImage" />
        <ScrollTop
            id="user-manual-page_scroll-top"
            :threshold="100"
            icon="pi pi-arrow-up"
            :pt="{
                root: 'w-2rem h-2rem bg-primary text-white',
                icon: {
                    class: 'text-base w-1rem h-1rem',
                },
            }"
        />
    </div>
    <Footer />
</template>

<style>
/* The manual's chapters are white panels on the grey app ground */
body {
    background-color: var(--p-surface-100) !important;
}
</style>

<style scoped>
.header-section {
    position: sticky;
    z-index: 10;
    background-color: var(--p-surface-100);
    padding-top: 0.75rem;
    padding-bottom: 0.75rem;
}
</style>
